/**
 * PresenceSystem — makes the other player's existence felt.
 *
 * Three layers:
 *   1. FADING TRAILS — every player leaves a soft light trail behind
 *      their orb as they move. Trails fade over ~1.8 seconds.
 *   2. BOND LINE — when two player orbs are within ~100px of each
 *      other, a glowing thread connects them. Brightness scales with
 *      proximity.
 *   3. TOGETHERNESS — if both orbs stay within bond range for 5+
 *      seconds, we fire a "togetherness" event: petals drift along
 *      the line between them, and we request a special Grandma letter.
 */
import { LetterManager } from './LetterManager.js';
import { SFX } from './SFX.js';

const BOND_RADIUS = 100;     // px distance threshold
const BOND_FADE_IN = 0.5;    // alpha ceiling when orbs are right on top of each other
const TOGETHERNESS_SECONDS = 5;
const TRAIL_LIFE_MS = 1800;
const TRAIL_SPAWN_INTERVAL_MS = 110;

export class PresenceSystem {
  constructor(scene) {
    this.scene = scene;
    this._trails = [];               // [{ circle, spawnedAt }]
    this._lastTrailSpawn = new Map(); // playerId -> timestamp
    this._bondLine = null;           // Phaser.Graphics
    this._bondStartedAt = null;      // when the current bond began
    this._togethernessFired = false; // one togetherness letter per bond session
  }

  /** Called every frame from GardenScene.update */
  update(time, delta) {
    this._spawnTrails();
    this._fadeTrails(delta);
    this._updateBond(delta);
  }

  _spawnTrails() {
    const scene = this.scene;
    if (!scene._players) return;
    const now = Date.now();
    for (const [playerId, player] of scene._players) {
      const last = this._lastTrailSpawn.get(playerId) || 0;
      if (now - last < TRAIL_SPAWN_INTERVAL_MS) continue;
      // Only spawn a trail if the player has moved at least a few px since last spawn
      const lastPos = player._lastTrailPos;
      if (lastPos && Math.hypot(lastPos.x - player.x, lastPos.y - player.y) < 3) {
        continue;
      }
      this._lastTrailSpawn.set(playerId, now);
      player._lastTrailPos = { x: player.x, y: player.y };

      const color = player.color || 0xffd54f;
      // Soft outer halo
      const halo = scene.add.circle(player.x, player.y, 10, color, 0.22).setDepth(28);
      // Bright inner dot
      const core = scene.add.circle(player.x, player.y, 3, color, 0.55).setDepth(29);
      this._trails.push({ halo, core, spawnedAt: now });
    }
  }

  _fadeTrails(delta) {
    const now = Date.now();
    const kept = [];
    for (const t of this._trails) {
      const age = now - t.spawnedAt;
      if (age >= TRAIL_LIFE_MS) {
        t.halo.destroy();
        t.core.destroy();
        continue;
      }
      const progress = age / TRAIL_LIFE_MS;
      t.halo.setAlpha(0.22 * (1 - progress));
      t.core.setAlpha(0.55 * (1 - progress));
      t.halo.setScale(1 + progress * 0.6);
      kept.push(t);
    }
    this._trails = kept;
  }

  _updateBond(delta) {
    const scene = this.scene;
    if (!scene._players || !scene._myPlayer) return;

    // Find the nearest OTHER player
    let nearest = null;
    let nearestDist = Infinity;
    for (const [playerId, player] of scene._players) {
      if (player === scene._myPlayer) continue;
      const d = Math.hypot(player.x - scene._myPlayer.x, player.y - scene._myPlayer.y);
      if (d < nearestDist) { nearestDist = d; nearest = player; }
    }

    if (!nearest || nearestDist > BOND_RADIUS) {
      // No bond right now — clear line + reset timer
      if (this._bondLine) { this._bondLine.destroy(); this._bondLine = null; }
      this._bondStartedAt = null;
      this._togethernessFired = false;
      return;
    }

    // We have a bond. Draw or update the line.
    const me = scene._myPlayer;
    const them = nearest;
    if (!this._bondLine) {
      this._bondLine = scene.add.graphics().setDepth(33);
      this._bondStartedAt = Date.now();
    }

    const alpha = BOND_FADE_IN * (1 - nearestDist / BOND_RADIUS);
    this._bondLine.clear();
    // Warm glow layer
    this._bondLine.lineStyle(6, 0xfff4a0, alpha * 0.4);
    this._bondLine.beginPath();
    this._bondLine.moveTo(me.x, me.y);
    this._bondLine.lineTo(them.x, them.y);
    this._bondLine.strokePath();
    // Bright thread layer
    this._bondLine.lineStyle(1.5, 0xfff8d4, alpha);
    this._bondLine.beginPath();
    this._bondLine.moveTo(me.x, me.y);
    this._bondLine.lineTo(them.x, them.y);
    this._bondLine.strokePath();

    // Small sparkles drifting along the line
    if (Math.random() < 0.08) {
      const t = Math.random();
      const sx = me.x + (them.x - me.x) * t;
      const sy = me.y + (them.y - me.y) * t;
      const sp = scene.add.circle(sx, sy, 1.2, 0xfff4a0, 0.9).setDepth(34);
      scene.tweens.add({
        targets: sp,
        y: sy - 12 - Math.random() * 12,
        alpha: 0,
        duration: 1200,
        onComplete: () => sp.destroy(),
      });
    }

    // Togetherness detection — sustained bond
    if (!this._bondStartedAt) this._bondStartedAt = Date.now();
    const sustained = (Date.now() - this._bondStartedAt) / 1000;
    if (sustained >= TOGETHERNESS_SECONDS && !this._togethernessFired) {
      this._togethernessFired = true;
      this._fireTogethernessMoment(me, them, Math.floor(sustained));
    }
  }

  _fireTogethernessMoment(me, them, seconds) {
    const scene = this.scene;
    SFX.memoryUnlock();
    if (navigator.vibrate) navigator.vibrate([20, 40, 20]);

    // Rain of petals drifting from the line between them upward
    for (let i = 0; i < 18; i++) {
      const t = Math.random();
      const sx = me.x + (them.x - me.x) * t;
      const sy = me.y + (them.y - me.y) * t;
      const petal = scene.add.circle(sx, sy, 2 + Math.random() * 2, 0xfff4a0, 1).setDepth(36);
      scene.tweens.add({
        targets: petal,
        x: sx + (Math.random() - 0.5) * 60,
        y: sy - 60 - Math.random() * 80,
        alpha: { from: 1, to: 0 },
        scale: { from: 1, to: 0.4 },
        duration: 2500 + Math.random() * 1500,
        ease: 'Sine.easeOut',
        onComplete: () => petal.destroy(),
      });
    }

    // Banner
    const w = scene.scale.width;
    const banner = scene.add.text(w / 2, 110, 'you stood beside her', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '17px',
      fontStyle: 'italic',
      color: '#fff4a0',
      stroke: '#0a0612',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(95).setAlpha(0);
    scene.tweens.add({
      targets: banner, alpha: { from: 0, to: 1 },
      duration: 700, yoyo: true, hold: 2800, ease: 'Sine.easeInOut',
      onComplete: () => banner.destroy(),
    });

    // Request a special letter
    LetterManager.requestLetter({
      event: 'togetherness_moment',
      togetherness: true,
      togethernessSeconds: seconds,
      plantsSoFar: [...scene._plantsTendedThisSession],
      secondsInGarden: Math.floor((Date.now() - scene._sessionStart) / 1000),
    });
  }

  cleanup() {
    for (const t of this._trails) { t.halo.destroy(); t.core.destroy(); }
    this._trails = [];
    if (this._bondLine) { this._bondLine.destroy(); this._bondLine = null; }
    this._bondStartedAt = null;
    this._togethernessFired = false;
  }
}
